import { useState } from "react";
import { Menu, Terminal } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import ThemeToggle from "./ThemeToggle";

const navLinks = [
  { href: "#about", label: "About", isHash: true },
  { href: "#services", label: "Services", isHash: true },
  { href: "#skills", label: "Skills", isHash: true },
  { href: "/ai-tools", label: "AI Tools", isHash: false },
  { href: "/wifi-hack-device", label: "Wifi Device", isHash: false },
  { href: "/veo-bunny-flow", label: "Veo 3 Video", isHash: false },
  { href: "/academy", label: "Academy", isHash: false },
  { href: "#contact", label: "Contact", isHash: true },
];

const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, isHash: boolean) => {
  if (isHash) {
    const isHomePage = window.location.pathname === "/";
    if (isHomePage) {
      e.preventDefault();
      const id = href.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        const offset = 80;
        const y = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    } else {
      e.preventDefault();
      window.location.href = "/" + href;
    }
  }
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const backdropBlur = useTransform(scrollY, [0, 200], [8, 20]);
  const borderOpacity = useTransform(scrollY, [0, 100], [0, 1]);

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 z-50 bg-background/90 transition-colors duration-300"
      style={{
        backdropFilter: useTransform(backdropBlur, (v) => `blur(${v}px)`),
        borderBottom: useTransform(borderOpacity, (v) => `1px solid hsl(var(--border) / ${v})`),
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <Terminal className="w-5 h-5 text-primary" />
            <span className="font-mono font-bold text-primary text-glow text-xs sm:text-sm">
              <span className="inline sm:hidden">Evil Markhors</span>
              <span className="hidden sm:inline">Evil Markhors Services</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              link.isHash ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href, link.isHash)}
                  className="text-sm font-mono text-muted-foreground hover:text-primary transition-colors"
                >
                  <span className="text-secondary">$</span> {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm font-mono text-muted-foreground hover:text-primary transition-colors"
                >
                  <span className="text-secondary">$</span> {link.label}
                </Link>
              )
            ))}
            <ThemeToggle />
          </div>

          <div className="flex items-center gap-2.5 md:hidden">
            <ThemeToggle />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button 
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-border bg-card hover:bg-muted text-foreground transition-colors" 
                  aria-label="Open menu"
                >
                  <Menu className="w-4 h-4 text-primary" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-card border-l border-border p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex items-center gap-2 px-6 py-5 border-b border-border">
                  <Terminal className="w-5 h-5 text-primary" />
                  <span className="font-mono font-bold text-primary text-glow">Evil Markhors Services</span>
                </div>
                <nav className="flex flex-col px-4 py-4 gap-1">
                  {navLinks.map((link, i) => (
                    link.isHash ? (
                      <a
                        key={link.href}
                        href={link.href}
                        onClick={(e) => {
                          setOpen(false);
                          handleNavClick(e, link.href, link.isHash);
                        }}
                        className="flex items-center gap-3 px-4 py-3 rounded-md text-sm font-mono text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                        style={{ animationDelay: `${i * 50}ms` }}
                      >
                        <span className="text-secondary">$</span>
                        <span>{link.label}</span>
                      </a>
                    ) : (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-md text-sm font-mono text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                        style={{ animationDelay: `${i * 50}ms` }}
                      >
                        <span className="text-secondary">$</span>
                        <span>{link.label}</span>
                      </Link>
                    )
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;
